import { useMemo } from "react";

import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import useTypedContext from "hooks/useTypedContext";
import KeyValue from "components/KeyValue";
import ValuesList from "components/ValuesList";
import { VcsProvider } from "types/generated";
import { providerHasVcsHost } from "utils/vcs";

import { StackVcsFormFields } from "../types";
import { StackFormContext } from "../context";

type NewStackSummaryVcsProps = {
  data: StackVcsFormFields;
};

const NewStackSummaryVcs = ({ data }: NewStackSummaryVcsProps) => {
  const { internalData } = useTypedContext(StackFormContext);

  const additionalProjectGlobsValues = useMemo(
    () => data.additionalProjectGlobs?.map((glob) => glob.value),
    [data.additionalProjectGlobs]
  );

  const gitSparseCheckoutPathsValues = useMemo(
    () => data.gitSparseCheckoutPaths?.map((path) => path.value),
    [data.gitSparseCheckoutPaths]
  );

  const isSpaceliftProvider = data.provider === VcsProvider.Spacelift;
  // A provider without a VCS host reads an archive from a folder prefix in an object store:
  // no repository to name, and the branch the form carries is only a placeholder for the HEAD
  // pointer. Blob Storage is the only one today.
  const hasVcsHost = providerHasVcsHost(data.provider);

  const repositoryLabel = hasVcsHost ? "Repository" : "Folder prefix";
  const repositoryValue =
    data.namespace && !isSpaceliftProvider
      ? `${data.namespace}/${data.repository}`
      : data.repository;

  return (
    <>
      <KeyValue name="Provider">{data.provider && HUMANIZE_PROVIDER[data.provider]}</KeyValue>

      {data.vcsIntegrationId && (
        <KeyValue name="Integration">
          {internalData.vcsIntegrationLabel || data.vcsIntegrationId}
        </KeyValue>
      )}

      {data.repository && <KeyValue name={repositoryLabel}>{repositoryValue}</KeyValue>}
      {!isSpaceliftProvider && hasVcsHost && <KeyValue name="Branch">{data.branch}</KeyValue>}
      {data.repositoryURL && <KeyValue name="Repository URL">{data.repositoryURL}</KeyValue>}
      {data.projectRoot && <KeyValue name="Project root">{data.projectRoot}</KeyValue>}
      <KeyValue name="Additional project globs">
        {additionalProjectGlobsValues?.length && (
          <ValuesList values={additionalProjectGlobsValues} />
        )}
      </KeyValue>
      <KeyValue name="Git sparse checkout paths">
        {gitSparseCheckoutPathsValues?.length && (
          <ValuesList values={gitSparseCheckoutPathsValues} />
        )}
      </KeyValue>
    </>
  );
};

export default NewStackSummaryVcs;
